export const DEFAULT_VIDEO_CALL_BACKGROUND = "#8D959C";
export const VIDEO_CALL_BACKGROUND_PRESETS = [
    { id: "default", color: DEFAULT_VIDEO_CALL_BACKGROUND, labelKey: "settings.appearance.videoCallBackgroundDefault" },
    { id: "blue", color: "#B8C4D6", labelKey: "settings.appearance.videoCallBackgroundBlue" },
    { id: "brown", color: "#C8B8AF", labelKey: "settings.appearance.videoCallBackgroundBrown" },
];
const DARK_TEXT = "#101720";
const LIGHT_TEXT = "#FFFFFF";
const PANEL_SURFACE_RATIO = 0.93;
const STORAGE_KEY = "dw_video_call_background";
const CHANGE_EVENT = "dw-video-call-background-change";
const HEX_COLOR = /^#[0-9a-f]{6}$/i;
/** Pick the higher WCAG contrast against the panels' shaded surface.
 * Device labels and desktop header title/status live on
 * --voice-call-panel-background (93% base), not inside the dark bubbles.
 * Keep this opaque black/white choice separate from the mobile/guest palette.
 */
export function getVideoCallSurfaceText(color) {
    const hex = HEX_COLOR.test(color) ? color : DEFAULT_VIDEO_CALL_BACKGROUND;
    const channels = [1, 3, 5].map((offset) => Math.round(parseInt(hex.slice(offset, offset + 2), 16) * PANEL_SURFACE_RATIO));
    const luminance = relativeLuminance(channels[0], channels[1], channels[2]);
    return (luminance + 0.05) / 0.05 >= 1.05 / (luminance + 0.05) ? "#000000" : "#FFFFFF";
}
function relativeLuminance(red, green, blue) {
    const channels = [red, green, blue].map((channel) => {
        const normalized = channel / 255;
        return normalized <= 0.04045
            ? normalized / 12.92
            : ((normalized + 0.055) / 1.055) ** 2.4;
    });
    return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
}
function contrastRatio(first, second) {
    const lighter = Math.max(first, second);
    const darker = Math.min(first, second);
    return (lighter + 0.05) / (darker + 0.05);
}
export function getVideoCallTextPalette(background) {
    const safeBackground = HEX_COLOR.test(background) ? background : DEFAULT_VIDEO_CALL_BACKGROUND;
    const channels = [1, 3, 5].map((start) => Math.round(parseInt(safeBackground.slice(start, start + 2), 16) * PANEL_SURFACE_RATIO));
    const panelLuminance = relativeLuminance(channels[0], channels[1], channels[2]);
    const darkLuminance = relativeLuminance(0x10, 0x17, 0x20);
    const lightLuminance = relativeLuminance(0xff, 0xff, 0xff);
    const useLightText = contrastRatio(lightLuminance, panelLuminance) >= contrastRatio(darkLuminance, panelLuminance);
    return useLightText
        ? { primary: LIGHT_TEXT, muted: "rgba(255, 255, 255, 0.72)" }
        : { primary: DARK_TEXT, muted: "rgba(16, 23, 32, 0.72)" };
}
export function getVideoCallBackground() {
    if (typeof window === "undefined")
        return DEFAULT_VIDEO_CALL_BACKGROUND;
    try {
        const stored = window.localStorage.getItem(STORAGE_KEY);
        return stored && HEX_COLOR.test(stored) ? stored.toUpperCase() : DEFAULT_VIDEO_CALL_BACKGROUND;
    }
    catch {
        return DEFAULT_VIDEO_CALL_BACKGROUND;
    }
}
export function setVideoCallBackground(color) {
    if (!HEX_COLOR.test(color) || typeof window === "undefined")
        return;
    const normalized = color.toUpperCase();
    try {
        window.localStorage.setItem(STORAGE_KEY, normalized);
    }
    catch {
        // The live preference still applies when storage is unavailable.
    }
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: normalized }));
}
export function subscribeVideoCallBackground(onChange) {
    if (typeof window === "undefined")
        return () => { };
    const handleChange = () => onChange();
    window.addEventListener(CHANGE_EVENT, handleChange);
    window.addEventListener("storage", handleChange);
    return () => {
        window.removeEventListener(CHANGE_EVENT, handleChange);
        window.removeEventListener("storage", handleChange);
    };
}
